//App
import { LookupValueProps } from "../../../../types/lookupValue";
import useComponentGeneralStyles from "../../../styles";
import useLookupValue from "./hooks/useLookupValue";
import { LookupValueFormContainer } from "./LookupValueFormContainer";
import LookupValueView from "./LookupValueView";

//Common
import { MoreActionButton } from "../../../common/Buttons";
import PageHeader from "../../../common/PageHeader/PageHeader";
import Drawer from "../../../common/VFX-UI/Drawer/Drawer";
import Snackbar from "../../../common/VFX-UI/SnackBar/Snackbar";
import ConfirmationDialog from "../../../common/VFX-UI/ConfirmationDialog/ConfirmationDialog";
import MoreActionsMenu from "../../../common/MoreActionsMenu/MoreActionsMenu";
import SearchContainer from "../../../common/Forms/SearchContainer/SearchContainer";
import { Chip, DataGrid } from "../../../common/VFX-UI";

const LookupValue: React.FC<LookupValueProps> = (props) => {
    const { data: lookupValues, activateMutation, deleteMutation } = props;
    const generalStyles = useComponentGeneralStyles();

    const {
        state,
        formContext,
        rowEvents,
        actionColumnAnchorEl,
        toggleNewViewHandler,
        toggleViewHandler,
        toggleEditViewHandler,
        toggleDeactivateHandler,
        toggleActivateHandler,
        handleDeactivate,
        handleActivate,
        onShowSuccess,
        onAddSnackbarClose,
        handleActionClick,
        setActionColumnAnchorEl,
        handleActiveToggleChange,
    } = useLookupValue({
        deleteMutation: deleteMutation,
        activateMutation: activateMutation,
    });

    const openMenu = Boolean(actionColumnAnchorEl);

    const columns = [
        {
            dataField: "id",
            text: "ID",
            hidden: true,
        },
        {
            dataField: "name",
            text: "Name",
            sort: true,
        },
        {
            dataField: "description",
            text: "Description",
            sort: true,
        },
        {
            dataField: "isActive",
            text: "Status",
            sort: false,
            formatter: (cell: boolean) => {
                return <Chip label={cell ? "Active" : "Inactive"} />;
            },
        },
        {
            dataField: "action",
            text: "",
            style: { width: "25px" },
            isDummyField: true,
            formatter: () => {
                return (
                    <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
                );
            },
        },
    ];

    return (
        <>
            <PageHeader>{formContext?.lookupType?.name}</PageHeader>
            <div style={generalStyles.searchContainer}>
                <SearchContainer
                    activeToggle={formContext?.activeToggle ?? true}
                    handleActiveToggleChange={handleActiveToggleChange}
                    toggleNewViewHandler={formContext?.lookupTypeId !== null ? toggleNewViewHandler : undefined}
                />
            </div>

            <Drawer show={state.toggleNewForm}>
                {state.toggleNewForm && (
                    <LookupValueFormContainer
                        onClose={toggleNewViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleEditForm}>
                {state.toggleEditForm && formContext?.data && (
                    <LookupValueFormContainer
                        onClose={toggleEditViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleView}>
                {state.toggleView && formContext?.data && (
                    <LookupValueView
                        onClose={toggleViewHandler}
                        editAction={() => {
                            toggleEditViewHandler();
                        }}
                        toggleDeactivateHandler={toggleDeactivateHandler}
                        toggleActivateHandler={toggleActivateHandler}
                    />
                )}
            </Drawer>

            <DataGrid
                columns={columns}
                data={lookupValues ?? []}
                keyField="id"
                rowEvents={rowEvents}
                totalSize={formContext?.paging?.totalSize}
                pageSize={formContext?.pageSize}
                remote={true}
                page={formContext?.paging?.currentPage}
                handlePageChange={async (newPage, pageSize ,sortField, sortOrder) => {
                    if (formContext?.setCurrentPage && newPage) {
                        await formContext?.setCurrentPage(newPage);
                    }
                    if (formContext?.setCurrentPageSize && pageSize) {
                        await formContext?.setCurrentPageSize(pageSize);
                    }
                    if (formContext?.setSortBy && sortField && sortOrder) {
                        await formContext?.setSortBy({
                            field: sortField,
                            direction: sortOrder === "asc" ? 1 : -1,
                        });
                    }
                    formContext?.refetchGrid();
                }}
            />

            {actionColumnAnchorEl && (
                <MoreActionsMenu
                    isOpen={openMenu}
                    target={actionColumnAnchorEl}
                    toggle={() => {
                        setActionColumnAnchorEl(null);
                    }}
                    onViewClick={() => {
                        toggleViewHandler();
                        setActionColumnAnchorEl(null);
                    }}
                />
            )}

            <ConfirmationDialog
                open={state.isDeactivateDialogOpen}
                onClose={toggleDeactivateHandler}
                onConfirm={handleDeactivate}
                contentTitle={`Deactivate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            ></ConfirmationDialog>

            <ConfirmationDialog
                open={state.isActivateDialogOpen}
                onClose={toggleActivateHandler}
                onConfirm={handleActivate}
                contentTitle={`Activate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            />

            {state.showSuccessSnackbar && (
                <Snackbar
                    open={state.showSuccessSnackbar}
                    message={state.successMessage}
                    handleClose={onAddSnackbarClose}
                    severity="success"
                />
            )}
        </>
    );
};

export default LookupValue;
